// Fetching: the manifest, the frame's cities, the wind lattice, and the tile URL
// the field is cut from.
//
// This is the layer between the server's routes and the map's sources. It knows
// what to ask for and where to put the answer; it does not know what any of it
// looks like. The one exception used to be setCityData reaching for
// updateLegend — a continuous legend clips to the range actually loaded — which
// is now a `cityFC` subscription instead, so the fetching layer no longer has to
// know the legend exists.
//
// Every async path is guarded by a token compared after the await: a viewport
// pan fires these faster than they return, and the answer to a superseded
// request must be dropped rather than painted.

import { map } from './map/instance.ts';
import { state, store, emptyCities, EMPTY_FC } from './state.ts';
import type { CityCollection } from './state.ts';
import { windActive } from './wind-state.ts';
import { snapBbox, bboxParam, cityBboxStep, needsSelectedParam } from '../lib/viewport-snap.ts';
import { quantizeZoom, lonStepForZoom } from '../lib/wind-grid.ts';
import { VAR } from '../lib/variables.ts';
import type { VarKey } from '../lib/variables.ts';
import { evalRecipe } from '../lib/recipe.ts';
import type { Conditions } from '../lib/recipe.ts';
import type { Manifest } from '../lib/manifest.ts';
import type { GeoJSONSource } from 'maplibre-gl';

export async function loadManifest(): Promise<Manifest> {
  const getJson = async (url: string): Promise<unknown> => {
    const r = await fetch(url, { cache: 'no-store' });
    if (!r.ok) throw new Error(`${url} -> ${r.status}`);
    return r.json();
  };
  // Only the manifest: city names and metadata ride along with each viewport's
  // features, so there is no upfront index to download.
  const m = (await getJson('/data/manifest.json')) as Manifest;
  state.manifest = m;
  state.dates = m.dates;
  state.version = encodeURIComponent(m.generatedAt || ''); // cache-buster for frames
  return m;
}

// Tile URL template for the interpolated field of the current frame. MapLibre
// fills in {z}/{x}/{y} and fetches only the visible tiles. The manifest version
// busts the immutable tile cache when data is rebuilt.
export function fieldTileUrl(v: VarKey = state.variable, date: string = state.dates[state.dateIdx], bucket: string = state.bucket): string {
  // Absolute URL with the {z}/{x}/{y} placeholders left literal — MapLibre needs
  // an absolute template and rejects percent-encoded braces, so don't run this
  // through new URL().
  //
  // A custom score has no baked grid: the recipe rides in the URL and the server
  // evaluates it per tile. Because the URL differs per recipe, a recipe edit
  // flows through the same double-buffered frame swap as a date change — the old
  // recipe stays on screen untouched until the new one is fully loaded.
  const r = VAR[v] && VAR[v].custom ? `&r=${state.recipeCanon}` : '';
  return `${location.origin}/tiles/${v}/${date}/${bucket}/{z}/{x}/{y}.mvt?v=${state.version}${r}`;
}

// Load the cities in view for the current frame. The zoom decides which are big
// enough to send, so a world view costs ~133 dots rather than all 17k. One
// request carries every variable, so switching variable is a repaint, not a
// refetch.
// The last question actually asked, so an identical one is not asked twice.
// Cleared (not just ignored) whenever the layer is turned off, so switching it
// back on refetches rather than trusting data that was thrown away.
let lastCitiesKey: string | null = null;

export async function refreshCities() {
  const src = map.getSource('weather') as GeoJSONSource | undefined;
  if (!src) return;
  if (!state.citiesOn) {
    lastCitiesKey = null;
    setCityData(src, emptyCities());
    return;
  }
  const b = map.getBounds();
  const z = Math.floor(map.getZoom());
  const bbox = bboxParam(snapBbox(b.getWest(), b.getSouth(), b.getEast(), b.getNorth(), cityBboxStep(z)));
  const date = state.dates[state.dateIdx];
  // While a city briefing is open, ask the server to always include that city so
  // its highlight ring shows even when it's too small to draw at the current
  // zoom. Only when it actually needs asking for, though: `selected` is a cache
  // key nobody else shares, and a city the reader just CLICKED was by definition
  // drawn at this zoom, so the plain question already answers it. mz unknown —
  // a city opened from a URL or the search box — stays conservative and asks.
  const sel = state.view === 'city' && state.selectedCity && needsSelectedParam(state.selectedCity.mz, z)
    ? `&selected=${state.selectedCity.id}`
    : '';
  const key = `${date}|${state.bucket}|${bbox}|${z}|${sel}`;
  // The map moved but is asking the same question — most pans, and every
  // `moveend` the city flyTo produces. The dots on screen already answer it.
  if (key === lastCitiesKey) return;
  lastCitiesKey = key;
  const token = ++state.citiesToken;
  try {
    const res = await fetch(`/cities/${date}/${state.bucket}?bbox=${bbox}&z=${z}${sel}`);
    if (token !== state.citiesToken) return; // superseded
    // A refused answer is not an answer: forget the key, or the dedupe below
    // would treat the empty map as this viewport's settled result and never ask
    // again while the reader sits still.
    if (!res.ok) lastCitiesKey = null;
    const fc = res.ok ? ((await res.json()) as CityCollection) : emptyCities();
    if (token !== state.citiesToken) return;
    setCityData(src, fc);
  } catch {
    if (token === state.citiesToken) {
      lastCitiesKey = null;
      setCityData(src, emptyCities());
    }
  }
}

// Score each dot's "My kind of day" from the component values it already carries, and
// write it on as a property — dots are coloured/filtered by MapLibre expressions,
// which only read properties. Null (ocean gate, no coverage) leaves the property
// off so the existing ['has', v] filter hides the dot, the same honesty as the
// baked scores. Runs before every setData and again on a recipe edit, so a
// tweak repaints the dots from data already in hand — no refetch.
export function annotateCustom(fc: CityCollection) {
  for (const f of (fc && fc.features) || []) {
    const s = evalRecipe(f.properties as Conditions, state.recipe);
    if (s != null) f.properties.custom = Math.round(s);
    else delete f.properties.custom;
  }
}

// Publish the loaded cities to the map and remember them. The continuous-legend
// clip reads its "relevant range" off these — the values actually on screen this
// frame — because the manifest stats are all-time global extremes (−80…60 °C) that
// would leave the bar spanning temperatures nothing in view ever reaches.
// The loaded frame's cities, handed to the map and announced. A continuous
// legend clips to the range actually on screen, so it listens for this rather
// than being called from here — which is what lets the fetching layer stop
// knowing the legend exists.
function setCityData(src: GeoJSONSource, fc: CityCollection) {
  annotateCustom(fc);
  src.setData(fc);
  store.set({ cityFC: fc });
}

// [lo, hi] of a variable across the currently-loaded cities, or null if none carry it.
export function observedRange(varKey: VarKey): [number, number] | null {
  const feats = (state.cityFC && state.cityFC.features) || [];
  let lo = Infinity;
  let hi = -Infinity;
  for (const f of feats) {
    const n = Number(f.properties && f.properties[varKey]);
    if (!Number.isFinite(n)) continue;
    if (n < lo) lo = n;
    if (n > hi) hi = n;
  }
  return lo <= hi ? [lo, hi] : null;
}

// Load the wind arrows for the viewport. Same shape as refreshCities: one fetch
// per view, superseded responses dropped by token.
//
// No double-buffering, unlike the field. All that machinery exists because the
// field is a full-screen opaque wash, where a gap between frames flashes the dark
// base and reads as a flicker. Arrows are sparse marks on a transparent layer:
// setData reparses and repaints in one go, so there is no torn state to hide. The
// cost is that during playback the arrows can trail the date by a frame — exactly
// what the city dots already do, from this same pattern.
let lastWindKey: string | null = null;

export async function refreshWind() {
  const src = map.getSource('wind') as GeoJSONSource | undefined;
  if (!src) return;
  if (!windActive()) {
    lastWindKey = null;
    src.setData(EMPTY_FC);
    return;
  }
  const b = map.getBounds();
  // Fractional zoom, unlike /cities: the lattice snaps to half-steps of its own,
  // so flooring here would cost it a rung of spacing accuracy for nothing. But
  // send the half-step the lattice will use rather than two decimal places of
  // it — arrowLattice quantizes this internally, so the answer is IDENTICAL and
  // an unbounded cache dimension collapses to one value per half-zoom.
  const z = quantizeZoom(map.getZoom());
  // Snapped to the arrow spacing itself, which is the natural grain here: the
  // lattice is already world-anchored, so widening to the next node adds one
  // ring of arrows off-screen and nothing else. (Real viewports run ~780 nodes
  // against a 4000 cap, so the extra ring cannot push it into degrading.)
  const bbox = bboxParam(snapBbox(b.getWest(), b.getSouth(), b.getEast(), b.getNorth(), lonStepForZoom(z)));
  const date = state.dates[state.dateIdx];
  const key = `${date}|${state.bucket}|${bbox}|${z}`;
  if (key === lastWindKey) return;
  lastWindKey = key;
  const token = ++state.windToken;
  try {
    const res = await fetch(`/wind/${date}/${state.bucket}?bbox=${bbox}&z=${z}`);
    if (token !== state.windToken) return; // superseded
    if (!res.ok) lastWindKey = null; // see refreshCities: a refusal must stay retryable
    src.setData(res.ok ? await res.json() : EMPTY_FC);
  } catch {
    if (token === state.windToken) {
      lastWindKey = null;
      src.setData(EMPTY_FC);
    }
  }
}
